import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  selectedFile: File | null;
  fileName: string;
  setFileName: (name: string) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpload: () => void;
  uploading: boolean;
  formatFileSize: (bytes: number) => string;
  selectedPages: string[];
  setSelectedPages: (pages: string[]) => void;
  availablePages: any[];
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  selectedFile,
  fileName,
  setFileName,
  handleFileSelect,
  handleUpload,
  uploading,
  formatFileSize,
  selectedPages,
  setSelectedPages,
  availablePages,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="info">
            Upload PDF or DOCX documents that can be used for document generation with placeholder replacement (e.g., {'{field_name}'}, {'{date}'}).
          </Alert>
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
            sx={{ py: 2 }}
          >
            Select PDF or DOCX File
            <input
              type="file"
              accept=".pdf,.docx"
              hidden
              onChange={handleFileSelect}
            />
          </Button>
          
          {selectedFile && (
            <Alert severity="success">
              Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="File Name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            helperText="Optional: Override the default file name"
          />
          
          <FormControl fullWidth>
            <InputLabel>Share with Pages</InputLabel>
            <Select
              multiple
              value={selectedPages}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedPages(Array.isArray(value) ? value : []);
              }}
              input={<OutlinedInput label="Share with Pages" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.isArray(selected) ? selected.map((value: string) => {
                    const permission = availablePages.find(p => p.permissionName === value);
                    return (
                      <Chip key={value} label={permission?.displayName || value} size="small" />
                    );
                  }) : null}
                </Box>
              )}
            >
              {availablePages.map((permission) => (
                <MenuItem key={permission.permissionId} value={permission.permissionName}>
                  <Box>
                    <Typography variant="body2">{permission.displayName}</Typography>
                    {permission.description && (
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {uploading && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={!selectedFile || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
